// Admin Gamify data services.
//
// All admin gamify endpoints live under /api/admin/gamify/. Every function below
// accepts an optional `{ token, params }` so callers can pass the user's bearer
// token and an optional query-param object (filters are sent only when supplied).

import { apiCall } from '../apiHelper';

const ADMIN_GAMIFY_BASE = '/api/admin/gamify';

// Build "?a=b&c=d" from a plain object — skip empty/null/undefined.
function buildQuery(params) {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    usp.append(key, value);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// ---------- Dashboard (stats + branch cards) ----------

export function fetchAdminDashboard({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/dashboard/${buildQuery(params)}`, { token });
}

// ---------- Departments ----------

export function fetchDepartments({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/departments/${buildQuery(params)}`, { token });
}

// ---------- Onboarding profiles (branch list) ----------

export function fetchOnboardingProfiles({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/onboarding-profiles/${buildQuery(params)}`, { token });
}

// ---------- Point events ----------

export function fetchPointEvents({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/point-events/${buildQuery(params)}`, { token });
}

// ---------- Project branch tasks ----------

export function fetchProjectBranchTasks({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${buildQuery(params)}`, { token });
}

export function fetchProjectBranchTaskById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/`, { token });
}

// Full replace — backend expects every editable field in the body.
export function updateProjectBranchTask({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/`, {
    method: 'PUT',
    token,
    body,
  });
}

// Partial update — only the fields the user actually changed.
export function patchProjectBranchTask({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/`, {
    method: 'PATCH',
    token,
    body,
  });
}

// Assign employees to a task. `replace: true` swaps the whole assignee set;
// `replace: false` appends to it.
export function assignProjectBranchTask({ token, id, employeeIds, replace = false } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/assign/`, {
    method: 'POST',
    token,
    body: { assigned_employee_ids: employeeIds, replace },
  });
}

export function completeProjectBranchTask({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/complete/`, {
    method: 'POST',
    token,
  });
}

export function reopenProjectBranchTask({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-branch-tasks/${id}/reopen/`, {
    method: 'POST',
    token,
  });
}

// ---------- Projects ----------

export function fetchProjects({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${buildQuery(params)}`, { token });
}

export function fetchProjectById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/`, { token });
}

export function createProject({ token, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/`, {
    method: 'POST',
    token,
    body,
  });
}

// Full replace — backend expects every editable field in the body.
export function updateProject({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/`, {
    method: 'PUT',
    token,
    body,
  });
}

// Partial update — only changed fields.
export function patchProject({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/`, {
    method: 'PATCH',
    token,
    body,
  });
}

export function deleteProject({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/`, {
    method: 'DELETE',
    token,
  });
}

export function activateProject({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/activate/`, {
    method: 'POST',
    token,
  });
}

// Deactivate a project — mirrors activate. Backend flips status to INACTIVE.
export function deactivateProject({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/deactivate/`, {
    method: 'POST',
    token,
  });
}

// Mark a project as completed — backend sets status=COMPLETED and stamps
// completed_on. Use this when the branch onboarding is finished, not when a
// single task is closed.
export function completeProject({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/projects/${id}/complete/`, {
    method: 'POST',
    token,
  });
}

// ---------- Project tasks (admin view of generated tasks across all projects) ----------

export function fetchProjectTasks({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-tasks/${buildQuery(params)}`, { token });
}

export function fetchProjectTaskById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-tasks/${id}/`, { token });
}

// ---------- Project task completions (audit log of who completed what) ----------

export function fetchProjectTaskCompletions({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/project-task-completions/${buildQuery(params)}`, { token });
}

// ---------- Employee ↔ Department memberships ----------

export function fetchEmployeeDepartments({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/employee-departments/${buildQuery(params)}`, { token });
}

// Body shape per backend EmployeeDepartmentSerializer:
//   { employee_id?, employee_email?, department_code, is_primary?, is_active? }
// employee_email is preferred when the caller doesn't know the user's pk.
export function createEmployeeDepartment({ token, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/employee-departments/`, {
    method: 'POST',
    token,
    body,
  });
}

// ---------- Challenges ----------

export function fetchChallenges({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/${buildQuery(params)}`, { token });
}

export function fetchChallengeById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/${id}/`, { token });
}

// Body fields: title, description, rule_type, target_count, bonus_points,
// is_active, starts_at, ends_at, display_order.
export function createChallenge({ token, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/`, {
    method: 'POST',
    token,
    body,
  });
}

export function updateChallenge({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/${id}/`, {
    method: 'PUT',
    token,
    body,
  });
}

export function patchChallenge({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/${id}/`, {
    method: 'PATCH',
    token,
    body,
  });
}

export function deleteChallenge({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/challenges/${id}/`, {
    method: 'DELETE',
    token,
  });
}

// ---------- Task Templates ----------

export function fetchTaskTemplates({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/${buildQuery(params)}`, { token });
}

export function fetchTaskTemplateById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/${id}/`, { token });
}

export function createTaskTemplate({ token, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/`, {
    method: 'POST',
    token,
    body,
  });
}

export function updateTaskTemplate({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/${id}/`, {
    method: 'PUT',
    token,
    body,
  });
}

export function patchTaskTemplate({ token, id, body } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/${id}/`, {
    method: 'PATCH',
    token,
    body,
  });
}

export function deleteTaskTemplate({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/templates/${id}/`, {
    method: 'DELETE',
    token,
  });
}

// ---------- Task Assignments (flat list of all task → employee mappings) ----------

export function fetchTaskAssignments({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/task-assignments/${buildQuery(params)}`, { token });
}

// ---------- Swag Items (admin CRUD) ----------
// Image uploads use multipart/form-data — apiHelper's default JSON path is not
// used here. Callers build a FormData object and pass it as `formData`.

export function fetchAdminSwagItems({ token, params } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/${buildQuery(params)}`, { token });
}

export function fetchAdminSwagItemById({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/${id}/`, { token });
}

// Create a swag item. Accepts FormData so the caller can attach image_file.
export function createAdminSwagItem({ token, formData } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/`, {
    method: 'POST',
    token,
    formData,
  });
}

// Full replace — all editable fields required.
export function updateAdminSwagItem({ token, id, formData } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/${id}/`, {
    method: 'PUT',
    token,
    formData,
  });
}

// Partial update — only changed fields.
export function patchAdminSwagItem({ token, id, formData } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/${id}/`, {
    method: 'PATCH',
    token,
    formData,
  });
}

export function deleteAdminSwagItem({ token, id } = {}) {
  return apiCall(`${ADMIN_GAMIFY_BASE}/swag/items/${id}/`, {
    method: 'DELETE',
    token,
  });
}
